import { useRef } from "react";
import { Sparkles } from "lucide-react";
import { useAssistant } from "@/components/assistant/useAssistant";
import { AssistantThread } from "@/components/assistant/AssistantThread";
import { AssistantAskBox } from "@/components/assistant/AssistantAskBox";
import { KB_CURATED_CHIPS } from "@/components/assistant/assistantChips";

/**
 * Екран «База знань» (/kb) — навчальний центр помічника (TZ §4 / мокап v2).
 * Замінює §0-placeholder (NAV-1) живим помічником. P0-формула: пояснює роботу
 * CRM, не знає клієнтів; без джерела з KB — no-coverage (sources-gate у Thread).
 */
export function KbPage() {
  const inputRef = useRef<HTMLInputElement>(null);
  const { thread, ask, asking } = useAssistant("kb");

  let directorMode = false;
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    if (thread[i].result) {
      directorMode = thread[i].result?.accessMode === "readOnly";
      break;
    }
  }

  const focusInput = () => inputRef.current?.focus();
  const hasThread = thread.length > 0;

  return (
    <div className="mx-auto max-w-[760px] px-5 py-8">
      <div className="rounded-xl border border-border bg-card">
        {/* hero */}
        <div className="px-6 pb-5 pt-7 text-center">
          <div className="mx-auto mb-2.5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary-soft text-primary-dark">
            <Sparkles className="h-5 w-5" aria-hidden />
          </div>
          <h1 className="text-lg font-bold tracking-tight">Помічник по роботі з CRM</h1>
          <p className="mx-auto mt-1 max-w-md text-[13px] text-muted-foreground">
            Пояснює процеси, поля, кнопки і чому щось недоступно. Відповіді — лише з
            перевіреної бази знань.
          </p>

          <div className="mx-auto mt-4 max-w-xl text-left">
            <AssistantAskBox
              ref={inputRef}
              onAsk={ask}
              disabled={asking}
              size="lg"
              placeholder="Постав питання про роботу CRM…"
            />
            <div className="mt-2 text-center text-[11px] text-muted-foreground">
              Не вводьте назви клієнтів, ЄДРПОУ, телефони, номери договорів або накладних.
            </div>
          </div>
        </div>

        {/* часті питання (статичний кураторський список) — ховаємо, коли вже є тред */}
        {!hasThread && (
          <div className="px-6 pb-6">
            <div className="mb-2 text-center text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
              Часті питання
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {KB_CURATED_CHIPS.map((c) => (
                <button
                  key={c}
                  type="button"
                  disabled={asking}
                  onClick={() => ask(c)}
                  className="rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:border-primary/30 hover:bg-primary-soft disabled:opacity-50"
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* тред */}
        {hasThread && (
          <div className="border-t border-border-subtle px-6 py-5">
            <AssistantThread
              thread={thread}
              directorMode={directorMode}
              onRetry={ask}
              onAskAnother={focusInput}
            />
          </div>
        )}
      </div>
    </div>
  );
}
